import Cookies from 'js-cookie';
import React, {Suspense} from 'react';
import {Link, Redirect, Route, Switch, useLocation} from 'react-router-dom';
import InstructorAccessGuard from 'Components/InstructorAccessGuard';
import EmailTemplateComposer from 'Screens/EmailTemplateComposer/EmailTemplateComposer';
import EmptyPage from 'Screens/Lost/EmptyPage';
import {
  CourseManagementMotionProvider,
  CourseManagementRouteEntry,
} from 'Screens/TrainingManagement/courseManagementMotion';
import {courseManagementRouteLoaders} from 'Screens/TrainingManagement/courseManagementRouteLoaders';
import packageJson from '../package.json';
import {logout} from './Actions/AuthActions';
import {ACCESS_TOKEN, ACCESS_TOKEN_EXPIRY, getAccessToken} from './Api';
import {APP_COUNTRY} from './DataConstants';

const LAZY_COMPONENTS = {
  Login: React.lazy(() => import('Screens/Login/Login')),
  PasswordExpiryBanner: React.lazy(() => import('Components/PasswordExpiryBanner')),
  RedirectComponent: React.lazy(() => import('Screens/Redirect/Redirect')),
  RootDirectory: React.lazy(() => import('Screens/RootDirectory')),
  AccountDetails: React.lazy(() => import('Screens/AccountDetails')),
  AdminSideBar: React.lazy(() => import('Components/SideBar/AdminSideBar')),
  AdminSideBarExpander: React.lazy(() => import('Components/SideBar/AdminSideBarExpander')),
  B2BExportPage: React.lazy(() => import('Screens/B2BExportPage')),
  DesignSystemCatalog: React.lazy(() => import('design-system/catalog')),
  CpCheckInLab: React.lazy(() => import('Screens/CpCheckIn/lab')),
  CareOwnerIndex: React.lazy(() => import('Screens/CareOwner/CareOwnerIndex')),
  CPDashboard: React.lazy(() => import('Screens/CPDashboard/CPDashboard')),
  CPVisits: React.lazy(() => import('Screens/CPDashboard/CPVisits/index')),
  CPEngagementTracker: React.lazy(() => import('Screens/CPEngagementTracker')),
  CPGSTStatus: React.lazy(() => import('Screens/CPGSTStatus')),
  CPWaivers: React.lazy(() => import('Screens/CPWaivers')),
  ManageCareRecipient: React.lazy(() => import('Screens/ManageCareRecipientB2C/ManageCareRecipient')),
  CombinedCPRosterCalendarView: React.lazy(() => import('Screens/CPRoster/CombinedCPRosterCalendarView')),
  OfflinePaymentSettings: React.lazy(() => import('Screens/OfflinePaymentSettings')),
  PaymentsDashboard: React.lazy(() => import('Screens/PaymentsDashboard')),
  PCAppActivationDashboard: React.lazy(() => import('Screens/PCAppActivationDashboard')),
  PricingEngine: React.lazy(() => import('Screens/Pricing/PricingEngineIndex')),
  PriorityInbox: React.lazy(() => import('Screens/PriorityInbox')),
  RecruitmentWorkflowList: React.lazy(() => import('Screens/RecruitmentWorkflowList/RecruitmentWorkflowList')),
  RecruitmentWorkflowSettings: React.lazy(
    () => import('Screens/RecruitmentWorkflowSettings/RecruitmentWorkflowSettings'),
  ),
  STCJobs: React.lazy(() => import('Screens/ShortTermContract/STCJobs')),
  Visits: React.lazy(() => import('Screens/Visits')),
  ServiceCoordinatorOperator: React.lazy(() => import('Screens/ServiceCoordinatorOperator')),
  CP2MActionCenter: React.lazy(() => import('Screens/CP2MActionCenter')),
  StrikeDetail: React.lazy(() => import('Screens/CP2MActionCenter/StrikeDetail')),
  EmailDashboard: React.lazy(() => import('Screens/EmailDashboard/EmailDashboard')),
  TrainingManagement: React.lazy(courseManagementRouteLoaders.overview),
  CreateTrainingSchedule: React.lazy(courseManagementRouteLoaders.createSchedule),
  InstructorManagement: React.lazy(courseManagementRouteLoaders.instructors),
  EventDetails: React.lazy(courseManagementRouteLoaders.eventDetails),
  CpCheckIn: React.lazy(() => import('Screens/CpCheckIn')),
  CareProSignupForm: React.lazy(() => import('./External Pages/CareProSignupForm')),
  PaymentWebLink: React.lazy(() => import('./External Pages/PaymentWebLink')),
  PIQForm: React.lazy(() => import('./External Pages/PIQForm')),
  CareProProfile: React.lazy(() => import('./External Pages/CareProProfile')),
} as const;

const RouteLoadingFallback = () => <div className="route-loading" role="status" aria-label="Loading page" />;
const SidebarLoadingFallback = () => <div className="sidebar-loading" role="status" aria-label="Loading navigation" />;

const logOut = async () => {
  if (await logout()) {
    Cookies.remove('userid');
    Cookies.remove(ACCESS_TOKEN);
    localStorage.removeItem(ACCESS_TOKEN);
    localStorage.removeItem(ACCESS_TOKEN_EXPIRY);
    window.location.reload();
  }
};

const getEnvironment = () => {
  const apiUrl = process.env.REACT_APP_API_URL || '';
  if (apiUrl.includes('dev')) return 'dev';
  if (apiUrl.includes('testing')) return 'staging';
  return '';
};

/** UI labs (check-in fixtures, etc.) — local + staging/dev APIs only, never production. */
const isNonProductionUiLabEnabled = (
  process.env.NODE_ENV === 'development' || Boolean(getEnvironment())
);

const displayVersionAndCountryAndEnvironment = () => {
  const envString = getEnvironment();
  return envString ? ` ${packageJson.version}-${envString}-${APP_COUNTRY}` : '';
};

const NavBar = () => (
  <div className="navigation-bar h center">
    <img className="homage-logo" src="/homage_logo_white.png" alt="" onClick={logOut} />
    <span className="homage-title">HOMAGE{displayVersionAndCountryAndEnvironment()}</span>
  </div>
);

const AdminNavBar = () => {
  const userId = Cookies.get('userid');
  return (
    <div className="navigation-bar h center">
      {userId && (
        <Suspense fallback={<div />}>
          <LAZY_COMPONENTS.AdminSideBarExpander />
        </Suspense>
      )}
      <Link className="h vc" to="/">
        <img className="homage-logo" src="/homage_logo_white.png" alt="" />
        <span className="homage-title">HOMAGE{displayVersionAndCountryAndEnvironment()}</span>
      </Link>
      <div className="logout" onClick={logOut}>
        Logout
      </div>
    </div>
  );
};

const CourseManagementRoutes = () => (
  <CourseManagementMotionProvider>
    <Suspense fallback={<RouteLoadingFallback />}>
      <Switch>
        <Route exact path="/course-management/schedules/new">
          <CourseManagementRouteEntry><LAZY_COMPONENTS.CreateTrainingSchedule /></CourseManagementRouteEntry>
        </Route>
        <Route exact path="/course-management/instructors">
          <CourseManagementRouteEntry><LAZY_COMPONENTS.InstructorManagement /></CourseManagementRouteEntry>
        </Route>
        <Route exact path="/course-management/:eventId">
          <CourseManagementRouteEntry><LAZY_COMPONENTS.EventDetails /></CourseManagementRouteEntry>
        </Route>
        <Route exact path="/course-management">
          <CourseManagementRouteEntry><LAZY_COMPONENTS.TrainingManagement /></CourseManagementRouteEntry>
        </Route>
      </Switch>
    </Suspense>
  </CourseManagementMotionProvider>
);

const PrivateContentRoutes = () => (
  <Switch>
    <Route exact path="/"><LAZY_COMPONENTS.RootDirectory /></Route>
    <Route exact path="/account-details"><LAZY_COMPONENTS.AccountDetails /></Route>
    <Route exact path={['/careowner', '/careowner/:id/:cr_id?']}><LAZY_COMPONENTS.CareOwnerIndex /></Route>
    <Route exact path="/cr/:id/:co_id?/:service_type?"><LAZY_COMPONENTS.ManageCareRecipient /></Route>
    <Route exact path="/offline-payment-settings"><LAZY_COMPONENTS.OfflinePaymentSettings /></Route>
    <Route exact path="/cp-waivers"><LAZY_COMPONENTS.CPWaivers /></Route>
    <Route path="/cp-gst-status"><LAZY_COMPONENTS.CPGSTStatus /></Route>
    <Route path="/stc"><LAZY_COMPONENTS.STCJobs /></Route>
    <Route path="/pricing"><LAZY_COMPONENTS.PricingEngine /></Route>
    <Route path="/priority-inbox"><LAZY_COMPONENTS.PriorityInbox /></Route>
    <Route path="/payments-dashboard"><LAZY_COMPONENTS.PaymentsDashboard /></Route>
    <Route path="/b2b/nok-activation-dashboard"><LAZY_COMPONENTS.PCAppActivationDashboard /></Route>
    <Route path="/recruitment-workflow-list"><LAZY_COMPONENTS.RecruitmentWorkflowList /></Route>
    <Route path="/recruitment-workflow-settings"><LAZY_COMPONENTS.RecruitmentWorkflowSettings /></Route>
    <Route path="/b2b/*"><LAZY_COMPONENTS.B2BExportPage /></Route>
    <Route path="/individual_visits"><LAZY_COMPONENTS.Visits /></Route>
    <Route path="/cp-dashboard"><LAZY_COMPONENTS.CPDashboard /></Route>
    <Route path="/cp-engagement-tracker"><LAZY_COMPONENTS.CPEngagementTracker /></Route>
    <Route path="/email-dashboard"><LAZY_COMPONENTS.EmailDashboard /></Route>
    <Route path="/course-management"><CourseManagementRoutes /></Route>
    <Route path="/sco-management"><LAZY_COMPONENTS.ServiceCoordinatorOperator /></Route>
    <Route path="/cp2m_action_center"><LAZY_COMPONENTS.CP2MActionCenter /></Route>
    <Route path="/strike/:id"><LAZY_COMPONENTS.StrikeDetail /></Route>
    <Route path="/standby_cp_roster"><LAZY_COMPONENTS.CombinedCPRosterCalendarView /></Route>
    <Route exact path="/cp-roster"><LAZY_COMPONENTS.CPVisits /></Route>
    <Route path="/cp-roster/:id"><LAZY_COMPONENTS.CPVisits /></Route>
    <Route path="/compose-email-template"><EmailTemplateComposer /></Route>
    <Redirect from="/" to="/login" />
  </Switch>
);

export const ProtectedLayout = React.memo(() => (
  <>
    <Suspense fallback={null}>
      <LAZY_COMPONENTS.PasswordExpiryBanner />
    </Suspense>
    <AdminNavBar />
    <div className="app-section">
      <Suspense fallback={<SidebarLoadingFallback />}>
        <LAZY_COMPONENTS.AdminSideBar />
      </Suspense>
      <main id="right" className="content-section internal-page-canvas">
        <div className="main-container">
          <Suspense fallback={<RouteLoadingFallback />}>
            <InstructorAccessGuard loadingFallback={<RouteLoadingFallback />}>
              <PrivateContentRoutes />
            </InstructorAccessGuard>
          </Suspense>
        </div>
      </main>
    </div>
  </>
));

export const AuthenticatedApp = () => {
  const location = useLocation();
  return getAccessToken()
    ? <ProtectedLayout />
    : <Redirect to={{pathname: '/login', state: {from: location}}} />;
};

const AppRoutes = () => (
  <Switch>
    {process.env.NODE_ENV === 'development' && (
      <Route exact path="/__design-system">
        <Suspense fallback={<div />}><LAZY_COMPONENTS.DesignSystemCatalog /></Suspense>
      </Route>
    )}
    {isNonProductionUiLabEnabled && (
      <Route exact path="/__cp-check-in">
        <Suspense fallback={<div />}><LAZY_COMPONENTS.CpCheckInLab /></Suspense>
      </Route>
    )}
    <Route exact path="/piq/apply">
      <Suspense fallback={<div />}><LAZY_COMPONENTS.PIQForm /></Suspense>
    </Route>
    <Route exact path="/public-carepro-profile">
      <Suspense fallback={<div />}><LAZY_COMPONENTS.CareProProfile /></Suspense>
    </Route>
    {!!(process.env.REACT_APP_CP_APPLY || !process.env.REACT_APP_DISABLE_VAL) && (
      <Route exact path={process.env.REACT_APP_DISABLE_VAL ? ['*'] : ['/carepro/apply']}>
        <Suspense fallback={<div />}><LAZY_COMPONENTS.CareProSignupForm /></Suspense>
      </Route>
    )}
    {!!(process.env.REACT_APP_PAYMENTS_DOMAIN || !process.env.REACT_APP_DISABLE_VAL) && (
      <Route
        exact
        path={process.env.REACT_APP_PAYMENTS_DOMAIN
          ? ['/:paymentId', '/:paymentId/*']
          : ['/weblink/:paymentId', '/weblink/:paymentId/*']}>
        <Suspense fallback={<div />}><LAZY_COMPONENTS.PaymentWebLink /></Suspense>
      </Route>
    )}
    <Route exact path="/lost"><NavBar /><EmptyPage /></Route>
    <Route exact path="/login"><NavBar /><Suspense fallback={<div />}><LAZY_COMPONENTS.Login /></Suspense></Route>
    <Route path="/redirect"><NavBar /><Suspense fallback={<div />}><LAZY_COMPONENTS.RedirectComponent /></Suspense></Route>
    <Route exact path="/cp-dashboard/care-pro-tracker"><Redirect to="/cp-engagement-tracker" /></Route>
    <Route exact path="/check-in/:checkInId"><Suspense fallback={<div />}><LAZY_COMPONENTS.CpCheckIn /></Suspense></Route>
    <Route path="/"><AuthenticatedApp /></Route>
  </Switch>
);

export default AppRoutes;
